import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@env/environment';
import {
  PaymentBrand,
  PaymentSimulationOptions,
  PaymentSimulationRecord,
  PaymentSimulationScenario,
  PaymentSimulatorConfig
} from '@interfaces/';
import { DataSaleInt } from '@interfaces/payment.interface';
import { PaymentDeviceService } from '@services/devices/payment-device.service';
import { CussService } from '@services/cuss.service';

const STORAGE_KEY_TRANSACTIONS = 'am_kiosk_payment_simulator_transactions';
const STORAGE_KEY_CONFIG = 'am_kiosk_payment_simulator_config';
const MAX_STORED_TRANSACTIONS = 100;

@Injectable({
  providedIn: 'root'
})
export class PaymentSimulatorService {
  private simulatedTransactions: PaymentSimulationRecord[] = [];
  private simulatedTransactions$ = new BehaviorSubject<PaymentSimulationRecord[]>([]);
  private config: PaymentSimulatorConfig = {
    enabled: false,
    lastEnabled: 0,
    totalSimulations: 0
  };

  constructor(
    private paymentDevice: PaymentDeviceService,
    private cussService: CussService
  ) {
    this.loadFromStorage();
  }

  /**
   * Verifica si el simulador está habilitado
   * TRIPLE PROTECCIÓN: environment.isCert + config.enabled + !environment.production
   */
  isSimulatorEnabled(): boolean {
    return environment.isCert === true &&
           this.config.enabled === true &&
           environment.production === false;
  }

  /**
   * Habilita el simulador (solo en CERT)
   */
  enableSimulator(): boolean {
    if (!environment.isCert || environment.production) {
      return false;
    }

    this.config.enabled = true;
    this.config.lastEnabled = Date.now();
    this.saveConfig();
    return true;
  }

  /**
   * Deshabilita el simulador
   */
  disableSimulator(): void {
    this.config.enabled = false;
    this.saveConfig();
  }

  /**
   * Simula un pago y retorna la data de venta
   * El control de habilitación se hace desde Payment_And_Weighing_Test
   */
  simulatePayment(options: PaymentSimulationOptions): DataSaleInt {
    const sale: DataSaleInt = this.paymentDevice.startTransactionDummy(
      options.total,
      options.brand,
      options.scenario
    );

    const record: PaymentSimulationRecord = {
      id: uuidv4(),
      timestamp: Date.now(),
      date: new Date().toISOString(),
      brand: options.brand,
      amount: options.total,
      scenario: options.scenario,
      success: options.scenario === 'success',
      authCode: sale.TAUT || undefined,
      errorMessage: this.getErrorMessage(options.scenario),
      cardNumber: sale.CARD,
      kioskId: this.cussService.KioskID || 'UNKNOWN'
    };

    this.registerSimulation(record);
    this.config.totalSimulations++;
    this.saveConfig();

    return sale;
  }

  /**
   * Obtiene el observable de transacciones simuladas
   */
  getSimulatedTransactions(): Observable<PaymentSimulationRecord[]> {
    return this.simulatedTransactions$.asObservable();
  }

  /**
   * Obtiene el array de transacciones simuladas
   */
  getSimulatedTransactionsArray(): PaymentSimulationRecord[] {
    return [...this.simulatedTransactions];
  }

  /**
   * Obtiene la configuración del simulador
   */
  getConfig(): PaymentSimulatorConfig {
    return { ...this.config };
  }

  /**
   * Limpia todas las transacciones simuladas
   */
  clearSimulatedTransactions(): void {
    this.simulatedTransactions = [];
    this.simulatedTransactions$.next([]);
    this.saveToStorage();
  }

  /**
   * Elimina una transacción específica por ID
   */
  deleteTransaction(id: string): void {
    this.simulatedTransactions = this.simulatedTransactions.filter(t => t.id !== id);
    this.simulatedTransactions$.next([...this.simulatedTransactions]);
    this.saveToStorage();
  }

  /**
   * Exporta las transacciones a JSON
   */
  exportTransactions(): string {
    const exportData = {
      exportDate: new Date().toISOString(),
      kioskId: this.cussService.KioskID,
      totalTransactions: this.simulatedTransactions.length,
      transactions: this.simulatedTransactions
    };
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Obtiene estadísticas de las simulaciones
   */
  getStatistics() {
    const transactions = this.simulatedTransactions;
    const successful = transactions.filter(t => t.success).length;
    const failed = transactions.length - successful;

    const byBrand = transactions.reduce((acc, t) => {
      acc[t.brand] = (acc[t.brand] || 0) + 1;
      return acc;
    }, {} as Record<PaymentBrand, number>);

    const byScenario = transactions.reduce((acc, t) => {
      acc[t.scenario] = (acc[t.scenario] || 0) + 1;
      return acc;
    }, {} as Record<PaymentSimulationScenario, number>);

    const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);

    return {
      total: transactions.length,
      successful,
      failed,
      successRate: transactions.length > 0 ? (successful / transactions.length * 100).toFixed(2) : 0,
      byBrand,
      byScenario,
      totalAmount,
      averageAmount: transactions.length > 0 ? (totalAmount / transactions.length).toFixed(2) : 0
    };
  }

  /**
   * Registra una simulación y la persiste
   */
  private registerSimulation(record: PaymentSimulationRecord): void {
    this.simulatedTransactions.unshift(record);

    // Limitar el número de transacciones almacenadas
    if (this.simulatedTransactions.length > MAX_STORED_TRANSACTIONS) {
      this.simulatedTransactions = this.simulatedTransactions.slice(0, MAX_STORED_TRANSACTIONS);
    }

    this.simulatedTransactions$.next([...this.simulatedTransactions]);
    this.saveToStorage();
  }

  /**
   * Carga transacciones y config desde localStorage
   */
  private loadFromStorage(): void {
    try {
      const transactionsJson = localStorage.getItem(STORAGE_KEY_TRANSACTIONS);
      if (transactionsJson) {
        this.simulatedTransactions = JSON.parse(transactionsJson);
        this.simulatedTransactions$.next([...this.simulatedTransactions]);
      }

      const configJson = localStorage.getItem(STORAGE_KEY_CONFIG);
      if (configJson) {
        this.config = JSON.parse(configJson);
      }
    } catch (error) {
      // Error loading from storage
    }
  }

  /**
   * Guarda transacciones en localStorage
   */
  private saveToStorage(): void {
    try {
      const jsonData = JSON.stringify(this.simulatedTransactions);
      localStorage.setItem(STORAGE_KEY_TRANSACTIONS, jsonData);
    } catch (error) {
      // Error saving to storage
    }
  }

  /**
   * Guarda config en localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY_CONFIG,
        JSON.stringify(this.config)
      );
    } catch (error) {
      // Error saving config
    }
  }

  /**
   * Obtiene el mensaje de error según el escenario
   */
  private getErrorMessage(scenario: PaymentSimulationScenario): string | undefined {
    const errorMessages: Record<PaymentSimulationScenario, string | undefined> = {
      success: undefined,
      declined: 'Transaction declined by issuer',
      timeout: 'Transaction timeout',
      insufficient_funds: 'Insufficient funds',
      invalid_card: 'Invalid card number',
      network_error: 'Network communication error',
      use_pinpad: 'Manual entry required on Pinpad'
    };
    return errorMessages[scenario];
  }
}

